"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createEntity,
  CreateEntityField,
  Entity,
  EntityRelationInput,
  getEntityList,
  updateEntity,
  UpdateEntityField,
} from "@/services/entity.service";
import { useCallback, useMemo } from "react";
import { useInvalidateSchemaCache } from "@/hooks/schema/use-invalidate-schema-cache";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { useCustomerId } from "@/contexts/customer-id-context";

const itemsToFilter = [
  "entity",
  "entityfield",
  "pylouser",
  "entityrelation",
  "pylocustomer",
  "pyloaro",
  // stuff the admin panel needs
  "userprofile",
  "mainlayout",
  "customerconfig",
  "entityviewcomponentmapping",
  "pyloapikey",
  "pylomedia",
  "dashboardview",
  "entityview",
  "flownodemeta",
  "dashboard",
];

export const useEntitiesFromDb = () => {
  const queryClient = useQueryClient();
  const invalidateSchemaCache = useInvalidateSchemaCache();

  // Get customer ID from context (provided by EntityPrefetcher) for cache key isolation
  const customerId = useCustomerId();

  // Disabled on public pages (login, join) via useAuthenticatedQuery
  // Query key includes customerId so each customer has separate cache
  const { data: entities, isFetched: entitiesFetched } = useAuthenticatedQuery({
    queryKey: ["entities", customerId],
    queryFn: () => getEntityList(),
    // TODO: Invalidate on schema changes from other users (e.g., via websocket event)
    staleTime: Infinity,
  });

  // All entities from the database, sorted alphabetically
  const allAvailableEntities = useMemo(() => {
    if (!entities) return undefined;
    return entities.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [entities]);

  const availableEntities = useMemo(() => {
    const result =
      allAvailableEntities
        ?.filter((entity) => {
          return (
            !(entity?.name && itemsToFilter.includes(entity.name.toLowerCase())) &&
            !entity?.is_system_entity
          );
        })
        .filter((entity) => !!entity && !entity.is_system_entity)
        .sort((a, b) => a?.name.localeCompare(b?.name ?? "") ?? 0) || [];

    return result;
  }, [allAvailableEntities]);

  // Read a single entity straight from the list query cache. Used after a save to
  // re-init local form state from the SAME object the dirty-compare effect uses as
  // its baseline (selection ENTITY_DATA_SELECTION), so deep-equal holds afterwards.
  const getEntityFromCache = useCallback(
    (entityId: string) =>
      queryClient
        .getQueryData<Array<Entity>>(["entities", customerId])
        ?.find((e) => e.id === entityId),
    [queryClient, customerId],
  );

  const { mutateAsync: createEntityMutation, isPending: isCreatingEntity } = useMutation({
    mutationFn: async ({
      entityName,
      entityFields,
      entityRelations,
      isAro,
    }: {
      entityName: string;
      entityFields: Array<CreateEntityField>;
      entityRelations: Array<EntityRelationInput>;
      isAro: boolean;
    }) => {
      return await createEntity({
        entityName,
        entityFields,
        entityRelations,
        isAro,
      });
    },
    onSuccess: async () => {
      await invalidateSchemaCache();
    },
  });

  const { mutateAsync: updateEntityMutation, isPending: isUpdatingEntity } = useMutation({
    mutationFn: async ({
      entityId,
      entityName,
      entityFieldsToCreateOrUpdate,
      entityFieldIdsToDelete,
      entityRelationsToCreateOrUpdate,
      entityRelationIdsToDelete,
      isAro,
    }: {
      entityId: string;
      entityName?: string;
      entityFieldsToCreateOrUpdate: Array<UpdateEntityField | CreateEntityField>;
      entityFieldIdsToDelete: Array<string>;
      entityRelationsToCreateOrUpdate: Array<EntityRelationInput>;
      entityRelationIdsToDelete: Array<string>;
      isAro?: boolean;
    }) => {
      return await updateEntity({
        entityId,
        entityName,
        entityFieldsToCreateOrUpdate,
        entityFieldIdsToDelete,
        entityRelationsToCreateOrUpdate,
        entityRelationIdsToDelete,
        isAro,
      });
    },
    onSuccess: async () => {
      await invalidateSchemaCache();
    },
  });

  return {
    entities,
    entitiesFetched,
    availableEntities,
    allAvailableEntities,
    getEntityFromCache,
    createEntity: createEntityMutation,
    updateEntity: updateEntityMutation,
    isCreatingEntity,
    isUpdatingEntity,
  };
};
